import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    Form, 
    Input, 
    Switch, 
    InputNumber, 
    message,
    Tag,
    Spin,
    Dropdown,
    Space,
    Button
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faGlobe, 
    faCloudUpload, 
    faShieldAlt,
    faKey,
    faCloud,
    faComments,
    faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { SearchOutlined, DownOutlined, RightOutlined, FileOutlined } from '@ant-design/icons';
import axios from '../../../utility/axios';
import { useTranslation } from 'react-i18next';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/system-settings.css';



const OAUTH_PROVIDERS = {
    GOOGLE: {
        name: 'Google',
        icon: faKey,
        color: '#4285f4',
        fieldGroups: [
            {
                title: 'Credentials',
                fields: [
                    { key: 'CLIENT_ID', label: 'Client ID', description: 'Google OAuth Client ID', placeholder: 'Enter Google Client ID' },
                    { key: 'CLIENT_SECRET', label: 'Client Secret', description: 'Google OAuth Client Secret', placeholder: 'Enter Google Client Secret', isPassword: true },
                    { key: 'REDIRECT_URI', label: 'Redirect URI', description: 'Google OAuth Redirect URI', placeholder: 'https://example.com/auth/google/callback' }
                ]
            }
        ]
    },
    AZURE: {
        name: 'Microsoft Azure',
        icon: faCloud,
        color: '#00a4ef',
        fieldGroups: [
            {
                title: 'Credentials',
                fields: [
                    { key: 'CLIENT_ID', label: 'Client ID', description: 'Azure OAuth Client ID', placeholder: 'Enter Azure Client ID' },
                    { key: 'CLIENT_SECRET', label: 'Client Secret', description: 'Azure OAuth Client Secret', placeholder: 'Enter Azure Client Secret', isPassword: true },
                    { key: 'TENANT_ID', label: 'Tenant ID', description: 'Azure Tenant ID', placeholder: 'Enter Azure Tenant ID' },
                    { key: 'REDIRECT_URI', label: 'Redirect URI', description: 'Azure OAuth Redirect URI', placeholder: 'https://example.com/auth/azure/callback' }
                ]
            }
        ]
    },
    FACEBOOK: {
        name: 'Facebook',
        icon: faComments,
        color: '#1877f2',
        fieldGroups: [
            {
                title: 'Credentials',
                fields: [
                    { key: 'CLIENT_ID', label: 'Client ID', description: 'Facebook OAuth Client ID', placeholder: 'Enter Facebook Client ID' },
                    { key: 'CLIENT_SECRET', label: 'Client Secret', description: 'Facebook OAuth Client Secret', placeholder: 'Enter Facebook Client Secret', isPassword: true },
                    { key: 'REDIRECT_URI', label: 'Redirect URI', description: 'Facebook OAuth Redirect URI', placeholder: 'https://example.com/auth/facebook/callback' }
                ]
            }
        ]
    },
    TWITTER: {
        name: 'Twitter',
        icon: faComments,
        color: '#1da1f2',
        fieldGroups: [
            {
                title: 'Credentials',
                fields: [
                    { key: 'CLIENT_ID', label: 'Client ID', description: 'Twitter OAuth Client ID', placeholder: 'Enter Twitter Client ID' },
                    { key: 'CLIENT_SECRET', label: 'Client Secret', description: 'Twitter OAuth Client Secret', placeholder: 'Enter Twitter Client Secret', isPassword: true },
                    { key: 'REDIRECT_URI', label: 'Redirect URI', description: 'Twitter OAuth Redirect URI', placeholder: 'https://example.com/auth/twitter/callback' }
                ]
            }
        ]
    }
};

const FILE_TYPES = {
    'Documents': [
        { extension: '.pdf', description: 'PDF Document' },
        { extension: '.doc', description: 'Word Document' },
        { extension: '.docx', description: 'Word Document' },
        { extension: '.txt', description: 'Text File' },
        { extension: '.rtf', description: 'Rich Text Format' },
        { extension: '.odt', description: 'OpenDocument Text' },
    ],
    'Images': [
        { extension: '.jpg', description: 'JPEG Image' },
        { extension: '.jpeg', description: 'JPEG Image' },
        { extension: '.png', description: 'PNG Image' },
        { extension: '.gif', description: 'GIF Image' },
        { extension: '.bmp', description: 'Bitmap Image' },
        { extension: '.svg', description: 'SVG Image' },
    ],
    'Archives': [
        { extension: '.zip', description: 'ZIP Archive' },
        { extension: '.rar', description: 'RAR Archive' },
        { extension: '.7z', description: '7-Zip Archive' },
        { extension: '.tar', description: 'TAR Archive' },
    ],
    'Audio': [
        { extension: '.mp3', description: 'MP3 Audio' },
        { extension: '.wav', description: 'WAV Audio' },
        { extension: '.ogg', description: 'OGG Audio' },
        { extension: '.m4a', description: 'M4A Audio' },
    ],
    'Video': [
        { extension: '.mp4', description: 'MP4 Video' },
        { extension: '.avi', description: 'AVI Video' },
        { extension: '.mov', description: 'QuickTime Video' },
        { extension: '.wmv', description: 'Windows Media Video' },
    ],
    'Spreadsheets': [
        { extension: '.xls', description: 'Excel Spreadsheet' },
        { extension: '.xlsx', description: 'Excel Spreadsheet' },
        { extension: '.csv', description: 'CSV File' },
        { extension: '.ods', description: 'OpenDocument Spreadsheet' },
    ],
    'Presentations': [
        { extension: '.ppt', description: 'PowerPoint Presentation' },
        { extension: '.pptx', description: 'PowerPoint Presentation' },
        { extension: '.odp', description: 'OpenDocument Presentation' },
    ]
};

const SYSTEM_SECTION_IDS = ['general', 'oauth', 'file-upload'];
const SETTINGS_AUTOSAVE_DEBOUNCE_MS = 2000;

const parseSystemHash = (hash) => {
    if (!hash) return 'general';
    const sub = hash.startsWith('system-settings-') ? hash.slice(16) : hash;
    return SYSTEM_SECTION_IDS.includes(sub) ? sub : 'general';
};

const SystemSettings = ({ embedded, unifiedPage }) => {
    const { t, i18n } = useTranslation();
    const skipSaveAfterLanguageChangeRef = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [fileTypeSearch, setFileTypeSearch] = useState('');
    const [expandedProviders, setExpandedProviders] = useState({});

    const getInitialFileTypeSortConfig = () => {
        try {
            const saved = localStorage.getItem('fileUploadSortConfig');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validKeys = ['extension', 'description', 'enabled'];
                if (validKeys.includes(parsed.key) && ['ascend', 'descend'].includes(parsed.direction)) {
                    return parsed;
                }
            }
        } catch (_) {}
        return { key: 'extension', direction: 'ascend' };
    };
    const [fileTypeSortConfig, setFileTypeSortConfig] = useState(getInitialFileTypeSortConfig);
    
    const hashRaw = location.hash ? location.hash.slice(1) : '';
    const [activeSectionState, setActiveSectionState] = useState(() => parseSystemHash(hashRaw || (typeof window !== 'undefined' && window.location.hash ? window.location.hash.slice(1) : '')));
    const activeSection = unifiedPage ? parseSystemHash(hashRaw) : activeSectionState;
    const [fileSelections, setFileSelections] = useState({});
    const [oauthSettings, setOauthSettings] = useState({
        GOOGLE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
        AZURE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', TENANT_ID: '', REDIRECT_URI: '' },
        FACEBOOK: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
        TWITTER: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' }
    });

    // Available tabs configuration
    const availableTabs = [
        { 
            id: 'general', 
            label: t('admin.systemSettings.generalSettings'), 
            icon: faGlobe,
            description: t('admin.systemSettings.generalSettings')
        },
        { 
            id: 'file-upload', 
            label: t('admin.systemSettings.fileUploadSettings'), 
            icon: faCloudUpload,
            description: t('admin.systemSettings.fileUploadSettings')
        },
        { 
            id: 'oauth', 
            label: t('admin.systemSettings.oauthSettings'), 
            icon: faShieldAlt,
            description: t('admin.systemSettings.oauthSettingsDescription')
        },
    ];

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        if (initialLoading) return;
        const scrollArea = contentRef.current;
        const container = embedded ? scrollArea : scrollArea?.parentElement;
        const sub = parseSystemHash(location.hash ? location.hash.slice(1) : '');
        if (SYSTEM_SECTION_IDS.includes(sub)) {
            const el = document.getElementById(`system-settings-section-${sub}`);
            if (el && container) {
                const targetTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
                container.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
            } else if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [embedded, unifiedPage, location.hash, initialLoading]);

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        const scrollArea = contentRef.current;
        const container = embedded ? scrollArea : scrollArea?.parentElement;
        if (!container) return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const id = entry.target.id?.replace('system-settings-section-', '');
                        if (SYSTEM_SECTION_IDS.includes(id)) setActiveSectionState(id);
                        break;
                    }
                }
            },
            { root: container, rootMargin: '-72px 0px -48% 0px', threshold: 0.05 }
        );
        SYSTEM_SECTION_IDS.forEach(id => {
            const el = document.getElementById(`system-settings-section-${id}`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [embedded, unifiedPage]);

    useEffect(() => {
        const handler = () => {
            skipSaveAfterLanguageChangeRef.current = true;
        };
        i18n.on('languageChanged', handler);
        return () => { i18n.off('languageChanged', handler); };
    }, [i18n]);

    // Handle search change for file types
    const handleFileTypeSearchChange = (e) => {
        setFileTypeSearch(e.target.value);
    };

    // Handle provider expansion toggle
    const handleProviderToggle = (providerId) => {
        setExpandedProviders(prev => ({
            ...prev,
            [providerId]: !prev[providerId]
        }));
    };

    // Handle search change for OAuth providers
    // Handle toggle all file types
    const handleToggleAllFileTypes = () => {
        // Check if all are currently enabled
        const allEnabled = Object.values(fileSelections).every(value => value === true);
        
        // Toggle all to the opposite state
        const newSelections = {};
        Object.keys(fileSelections).forEach(extension => {
            newSelections[extension] = !allEnabled;
        });
        
        setFileSelections(newSelections);
        
        // Update form values
        Object.keys(newSelections).forEach(extension => {
            form.setFieldValue(['fileTypeSelections', extension], newSelections[extension]);
        });
    };

    const fileTypeSortMenuItems = [
        { key: 'extension', icon: FileOutlined, labelKey: 'sortByFileType' },
        { key: 'description', icon: FileOutlined, labelKey: 'sortByDescription' },
        { key: 'enabled', icon: FileOutlined, labelKey: 'sortByStatus' },
    ];
    const fileTypeSortDropdown = (
        <div className="system-settings-file-type-sort-dropdown">
            <Dropdown
                menu={{
                    items: fileTypeSortMenuItems.map(({ key, icon: Icon, labelKey }) => ({
                        key,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span><Icon style={{ marginRight: '8px' }} />{t(`admin.systemSettings.${labelKey}`)}</span>
                                {fileTypeSortConfig.key === key && (fileTypeSortConfig.direction === 'ascend' ? '↑' : '↓')}
                            </div>
                        ),
                        onClick: () => {
                            const next = { key, direction: fileTypeSortConfig.key === key && fileTypeSortConfig.direction === 'ascend' ? 'descend' : 'ascend' };
                            setFileTypeSortConfig(next);
                            localStorage.setItem('fileUploadSortConfig', JSON.stringify(next));
                        }
                    }))
                }}
                trigger={['click']}
            >
                <Button style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span>
                            {t('admin.systemSettings.sortBy')} {fileTypeSortConfig.key === 'extension' ? t('admin.systemSettings.sortByFileType') : fileTypeSortConfig.key === 'description' ? t('admin.systemSettings.sortByDescription') : t('admin.systemSettings.sortByStatus')} {fileTypeSortConfig.direction === 'ascend' ? '↑' : '↓'}
                        </span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
        </div>
    );

    const oauthProvidersData = useMemo(() => {
        const allProviders = Object.entries(OAUTH_PROVIDERS).map(([providerId, providerConfig]) => {
            const providerSettings = oauthSettings[providerId] || {};
            const totalFields = providerConfig.fieldGroups.reduce((acc, group) => acc + group.fields.length, 0);
            // Filter out ENABLED and only count string fields that are filled
            const filledFields = Object.entries(providerSettings)
                .filter(([key, val]) => key !== 'ENABLED' && typeof val === 'string' && val.trim() !== '')
                .length;
            
            return {
                providerId,
                name: providerConfig.name,
                icon: providerConfig.icon,
                color: providerConfig.color,
                description: `OAuth authentication provider with ${totalFields} configurable settings`,
                filledCount: filledFields,
                totalCount: totalFields,
                fieldGroups: providerConfig.fieldGroups,
                settings: providerSettings
            };
        });

        return allProviders;
    }, [oauthSettings]);

    const fileTypeData = useMemo(() => {
        // First create the full dataset
        const allTypes = Object.entries(FILE_TYPES)
            .flatMap(([category, types]) =>
                types.map(type => ({
                    ...type,
                    category,
                    key: type.extension
                }))
            );
        
        // Then apply search filter
        const filteredData = allTypes.filter(item => 
            !fileTypeSearch || 
            item.extension.toLowerCase().includes(fileTypeSearch.toLowerCase()) ||
            item.description.toLowerCase().includes(fileTypeSearch.toLowerCase()) ||
            item.category.toLowerCase().includes(fileTypeSearch.toLowerCase())
        );
        
        // Apply sorting
        const sortKey = fileTypeSortConfig.key;
        const sortDir = fileTypeSortConfig.direction === 'ascend' ? 1 : -1;
        const sortedData = [...filteredData].sort((a, b) => {
            let aValue, bValue;
            
            if (sortKey === 'enabled') {
                aValue = fileSelections[a.extension] || false;
                bValue = fileSelections[b.extension] || false;
                return sortDir * (aValue === bValue ? 0 : aValue ? -1 : 1);
            } else {
                aValue = a[sortKey];
                bValue = b[sortKey];
                
                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                    return sortDir * aValue.localeCompare(bValue);
                }
                
                return sortDir * (aValue > bValue ? 1 : aValue < bValue ? -1 : 0);
            }
        });
        
        return {
            data: sortedData,
            total: sortedData.length
        };
    }, [fileTypeSearch, fileTypeSortConfig, fileSelections]);

    const fetchSettings = useCallback(async () => {
        try {
            setInitialLoading(true);
            const response = await axios.get('/api/system-settings/');
            
            const allowedTypes = response.data.allowedFileTypes.split(',').filter(type => type);
            
            const fileTypeSelections = {};
            Object.values(FILE_TYPES).flat().forEach(({ extension }) => {
                fileTypeSelections[extension] = allowedTypes.includes(extension);
            });

            setFileSelections(fileTypeSelections);
            
            // Set OAuth settings - ensure we have all provider settings
            const defaultOauthSettings = {
                GOOGLE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
                AZURE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', TENANT_ID: '', REDIRECT_URI: '' },
                FACEBOOK: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
                TWITTER: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' }
            };
            
            let mergedOauthSettings = { ...defaultOauthSettings };
            if (response.data.oauthSettings) {
                // Merge server settings with defaults, ensuring all fields exist
                Object.keys(defaultOauthSettings).forEach(provider => {
                    if (response.data.oauthSettings[provider]) {
                        mergedOauthSettings[provider] = {
                            ...defaultOauthSettings[provider],
                            ...response.data.oauthSettings[provider]
                        };
                    }
                });
            }
            setOauthSettings(mergedOauthSettings);
            
            form.setFieldsValue({
                systemUrl: response.data.systemUrl,
                maxUploadSize: parseInt(response.data.maxUploadSize),
                previewPdfMaxSizeMb: parseInt(response.data.previewPdfMaxSizeMb) || 20,
                fileTypeSelections
            });
        } catch (error) {
            message.error(i18n.t('admin.systemSettings.loadError', { error: error.response?.data?.error || error.message }));
        } finally {
            setInitialLoading(false);
        }
    }, [form, i18n]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const submitRef = useRef(null);
    submitRef.current = () => form.submit();
    const skipNextSaveRef = useRef(true);
    const skipSaveAfterSubmitRef = useRef(false);
    const saveDebounceRef = useRef(null);

    useEffect(() => {
        if (initialLoading) return;
        if (skipNextSaveRef.current) {
            skipNextSaveRef.current = false;
            return;
        }
        if (skipSaveAfterSubmitRef.current) {
            skipSaveAfterSubmitRef.current = false;
            return;
        }
        if (skipSaveAfterLanguageChangeRef.current) {
            skipSaveAfterLanguageChangeRef.current = false;
            return;
        }
        const timer = setTimeout(() => submitRef.current?.(), SETTINGS_AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [initialLoading, fileSelections, oauthSettings]);

    const handleFormValuesChange = useCallback(() => {
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => form.submit(), SETTINGS_AUTOSAVE_DEBOUNCE_MS);
    }, [form]);

    const handleSubmit = async (values) => {
        const activeEl = document.activeElement;
        setLoading(true);
        try {
            const selectedFileTypes = Object.entries(fileSelections)
                .filter(([_, isSelected]) => isSelected)
                .map(([extension]) => extension)
                .join(',');

            const formData = {
                systemUrl: values.systemUrl?.trim(),
                maxUploadSize: Number(values.maxUploadSize) || 0,
                previewPdfMaxSizeMb: Number(values.previewPdfMaxSizeMb) || 20,
                allowedFileTypes: selectedFileTypes,
                oauthSettings: oauthSettings
            };
            
            const response = await axios.post('/api/system-settings/update/', formData);
            message.success(t('admin.systemSettings.updateSuccess'));

            skipSaveAfterSubmitRef.current = true;
            if (response.data.settings && response.data.settings.oauthSettings) {
                const defaultOauthSettings = {
                    GOOGLE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
                    AZURE: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', TENANT_ID: '', REDIRECT_URI: '' },
                    FACEBOOK: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' },
                    TWITTER: { ENABLED: false, CLIENT_ID: '', CLIENT_SECRET: '', REDIRECT_URI: '' }
                };
                
                let mergedOauthSettings = { ...defaultOauthSettings };
                Object.keys(defaultOauthSettings).forEach(provider => {
                    if (response.data.settings.oauthSettings[provider]) {
                        mergedOauthSettings[provider] = {
                            ...defaultOauthSettings[provider],
                            ...response.data.settings.oauthSettings[provider]
                        };
                    }
                });
                setOauthSettings(mergedOauthSettings);
            }
        } catch (error) {
            message.error(t('admin.systemSettings.updateError', { error: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
            if (activeEl && typeof activeEl.focus === 'function' && document.body.contains(activeEl)) {
                requestAnimationFrame(() => requestAnimationFrame(() => activeEl.focus({ preventScroll: true })));
            }
        }
    };

    if (initialLoading) {
        return (
            <div className="system-settings-container"></div>
        );
    }

    return (
        <div className={`system-settings-container ${embedded ? 'system-settings-embedded' : ''} ${unifiedPage ? 'system-settings-unified' : ''}`}>
            <div className="system-settings-card">
                <div className="system-settings-split">
                    <div className="system-settings-main">
                        <div className={`system-settings-scroll-area ${unifiedPage ? 'system-settings-scroll-area-unified' : ''}`} ref={contentRef} data-settings-scroll>
                        <Form form={form} onFinish={handleSubmit} onValuesChange={handleFormValuesChange} layout="vertical">
                            <div className="system-settings-content">
                        <div id="system-settings-section-general" className="system-settings-section system-settings-scroll-section">
                                <h2 className="system-settings-section-title">{t('admin.systemSettings.generalSettings')}</h2>
                                <div className="system-settings-form-container">
                                    
                                    <div className="system-settings-form-grid">
                                        <div className="system-settings-card-item">
                                            <div className="system-settings-card-header system-settings-card-header-with-input">
                                                <FontAwesomeIcon icon={faGlobe} className="system-settings-card-icon" />
                                                <div className="system-settings-card-title-section">
                                                    <label className="system-settings-card-title">{t('admin.systemSettings.systemUrl')}</label>
                                                    <p className="system-settings-card-description">{t('admin.systemSettings.systemUrlDescription')}</p>
                                                </div>
                                                <div className="system-settings-card-input-section system-settings-input-row">
                                                    <Form.Item
                                                        name="systemUrl"
                                                        rules={[{ required: true, type: 'url' }]}
                                                        style={{ margin: 0 }}
                                                    >
                                                        <Input
                                                            placeholder={t('admin.systemSettings.systemUrlPlaceholder')}
                                                            disabled={loading}
                                                            bordered={false}
                                                            className="system-settings-profile-style-input"
                                                        />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        <div id="system-settings-section-oauth" className="system-settings-section system-settings-scroll-section">
                                <h2 className="system-settings-section-title">{t('admin.systemSettings.oauthSettings')}</h2>
                                <div className="system-settings-oauth-container">
                                    <Spin spinning={loading}>
                                        <div className="oauth-providers-list">
                                            {oauthProvidersData.map((provider) => (
                                                <div key={provider.providerId} className="oauth-provider-item">
                                                    <div 
                                                        className="oauth-provider-header"
                                                    >
                                                        <div className="oauth-provider-info" onClick={() => handleProviderToggle(provider.providerId)}>
                                                            <div className="oauth-provider-icon">
                                                                <FontAwesomeIcon 
                                                                    icon={provider.icon} 
                                                                    style={{ color: provider.color, fontSize: '20px' }}
                                                                />
                                                            </div>
                                                            <div className="oauth-provider-details">
                                                                <h4 className="oauth-provider-name">{provider.name}</h4>
                                                                <p className="oauth-provider-description">{provider.description}</p>
                                                                <span className="oauth-provider-status">
                                                                    {provider.filledCount} of {provider.totalCount} settings filled
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="oauth-provider-enabled-toggle">
                                                            <Switch
                                                                checked={provider.settings.ENABLED || false}
                                                                onChange={(checked, event) => {
                                                                    event?.stopPropagation?.();
                                                                    const newOauthSettings = { ...oauthSettings };
                                                                    if (!newOauthSettings[provider.providerId]) {
                                                                        newOauthSettings[provider.providerId] = {};
                                                                    }
                                                                    newOauthSettings[provider.providerId].ENABLED = checked;
                                                                    setOauthSettings(newOauthSettings);
                                                                }}
                                                                onClick={(checked, event) => {
                                                                    event?.stopPropagation?.();
                                                                }}
                                                                disabled={loading}
                                                                size="default"
                                                            />
                                                        </div>
                                                        <div className="oauth-provider-toggle" onClick={() => handleProviderToggle(provider.providerId)}>
                                                            <div className={`oauth-chevron ${expandedProviders[provider.providerId] ? 'expanded' : ''}`}>
                                                                {expandedProviders[provider.providerId] ? <DownOutlined /> : <RightOutlined />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {expandedProviders[provider.providerId] && (
                                                        <div className="oauth-provider-settings">
                                                            {provider.fieldGroups.map((group, groupIndex) => (
                                                                <div key={groupIndex} className="oauth-settings-group">
                                                                    <div className="oauth-group-fields">
                                                                        {group.fields.map((field) => (
                                                                            <div key={field.key} className="oauth-field-item">
                                                                                <div className="oauth-field-info">
                                                                                    <span className="oauth-field-label">{field.label}</span>
                                                                                    <span className="oauth-field-description">{field.description}</span>
                                                                                </div>
                                                                                <div className="oauth-field-input">
                                                                                    {field.isPassword ? (
                                                                                        <Input.Password
                                                                                            value={provider.settings[field.key] || ''}
                                                                                            onChange={(e) => {
                                                                                                const newOauthSettings = { ...oauthSettings };
                                                                                                if (!newOauthSettings[provider.providerId]) {
                                                                                                    newOauthSettings[provider.providerId] = {};
                                                                                                }
                                                                                                newOauthSettings[provider.providerId][field.key] = e.target.value;
                                                                                                setOauthSettings(newOauthSettings);
                                                                                            }}
                                                                                            placeholder={field.placeholder}
                                                                                            disabled={loading}
                                                                                            bordered={false}
                                                                                            className="system-settings-profile-style-input"
                                                                                        />
                                                                                    ) : (
                                                                                        <Input
                                                                                            value={provider.settings[field.key] || ''}
                                                                                            onChange={(e) => {
                                                                                                const newOauthSettings = { ...oauthSettings };
                                                                                                if (!newOauthSettings[provider.providerId]) {
                                                                                                    newOauthSettings[provider.providerId] = {};
                                                                                                }
                                                                                                newOauthSettings[provider.providerId][field.key] = e.target.value;
                                                                                                setOauthSettings(newOauthSettings);
                                                                                            }}
                                                                                            placeholder={field.placeholder}
                                                                                            disabled={loading}
                                                                                            bordered={false}
                                                                                            className="system-settings-profile-style-input"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </Spin>
                                </div>
                            </div>
                        <div id="system-settings-section-file-upload" className="system-settings-section system-settings-file-upload-section system-settings-scroll-section">
                                <h2 className="system-settings-section-title">{t('admin.systemSettings.fileUploadSettings')}</h2>
                                <div className="system-settings-form-container">
                                    <div className="system-settings-form-grid">
                                        <div className="system-settings-card-item">
                                            <div className="system-settings-card-header system-settings-card-header-with-input">
                                                <FontAwesomeIcon icon={faCloudUpload} className="system-settings-card-icon" />
                                                <div className="system-settings-card-title-section">
                                                    <label className="system-settings-card-title">{t('admin.systemSettings.maxFileSize')}</label>
                                                    <p className="system-settings-card-description">{t('admin.systemSettings.maxFileSizeTooltip')}</p>
                                                </div>
                                                <div className="system-settings-card-input-section">
                                                    <div className="system-settings-file-upload-input-row">
                                                        <Form.Item
                                                            name="maxUploadSize"
                                                            rules={[{ required: true }]}
                                                            style={{ margin: 0 }}
                                                        >
                                                            <InputNumber
                                                                min={1}
                                                                max={1000}
                                                                placeholder="0"
                                                                disabled={loading}
                                                                className="system-settings-file-upload-number"
                                                            />
                                                        </Form.Item>
                                                        <span className="system-settings-file-upload-unit">{t('admin.systemSettings.unitMb')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="system-settings-card-item">
                                            <div className="system-settings-card-header system-settings-card-header-with-input">
                                                <FontAwesomeIcon icon={faFilePdf} className="system-settings-card-icon" />
                                                <div className="system-settings-card-title-section">
                                                    <label className="system-settings-card-title">{t('admin.systemSettings.previewPdfMaxSize')}</label>
                                                    <p className="system-settings-card-description">{t('admin.systemSettings.previewPdfMaxSizeTooltip')}</p>
                                                </div>
                                                <div className="system-settings-card-input-section">
                                                    <div className="system-settings-file-upload-input-row">
                                                        <Form.Item
                                                            name="previewPdfMaxSizeMb"
                                                            rules={[{ required: true }]}
                                                            style={{ margin: 0 }}
                                                        >
                                                            <InputNumber
                                                                min={1}
                                                                max={100}
                                                                placeholder="20"
                                                                disabled={loading}
                                                                className="system-settings-file-upload-number"
                                                            />
                                                        </Form.Item>
                                                        <span className="system-settings-file-upload-unit">{t('admin.systemSettings.unitMb')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="system-settings-file-type-search-row">
                                    <Input
                                        placeholder={t('admin.systemSettings.searchFileTypes')}
                                        prefix={<SearchOutlined />}
                                        value={fileTypeSearch}
                                        onChange={handleFileTypeSearchChange}
                                        allowClear
                                        disabled={loading}
                                        className="system-settings-file-type-search-input"
                                    />
                                    {fileTypeSortDropdown}
                                    <Button
                                        type="default"
                                        onClick={handleToggleAllFileTypes}
                                        disabled={loading}
                                        className="system-settings-file-type-toggle-all-btn"
                                    >
                                        {Object.values(fileSelections).every(v => v === true) ? t('admin.systemSettings.disableAll') : t('admin.systemSettings.enableAll')}
                                    </Button>
                                </div>
                                <Spin spinning={loading}>
                                    <div className={`system-settings-file-type-results ${unifiedPage ? 'settings-records-container' : ''}`} onWheel={unifiedPage ? (e) => e.stopPropagation() : undefined}>
                                    {fileTypeData.data.length === 0 ? (
                                        <div className="system-settings-file-type-empty">
                                            <FileOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                            <p>{t('admin.systemSettings.noFileTypesFound')}</p>
                                        </div>
                                    ) : (
                                        <div className="system-settings-file-type-cards-grid">
                                            {fileTypeData.data.map((record) => (
                                                <div
                                                    key={record.extension}
                                                    className="system-settings-file-type-record-card"
                                                    onClick={(e) => {
                                                        if (!e.target.closest('.ant-switch')) {
                                                            const newSelections = { ...fileSelections };
                                                            newSelections[record.extension] = !newSelections[record.extension];
                                                            setFileSelections(newSelections);
                                                            form.setFieldValue(['fileTypeSelections', record.extension], newSelections[record.extension]);
                                                        }
                                                    }}
                                                >
                                                    <Tag color="blue" className="system-settings-file-type-tag">{record.extension}</Tag>
                                                    <div className="system-settings-file-type-description">{record.description}</div>
                                                    <Switch
                                                        checked={fileSelections[record.extension]}
                                                                onChange={(checked, event) => {
                                                                    event?.stopPropagation?.();
                                                                    const newSelections = { ...fileSelections };
                                                                    newSelections[record.extension] = checked;
                                                                    setFileSelections(newSelections);
                                                                    form.setFieldValue(['fileTypeSelections', record.extension], checked);
                                                                }}
                                                                onClick={(checked, event) => event?.stopPropagation?.()}
                                                        checkedChildren={t('admin.systemSettings.enabled')}
                                                        unCheckedChildren={t('admin.systemSettings.disabled')}
                                                        size="default"
                                                        className="system-settings-file-type-switch"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    </div>
                                </Spin>
                            </div>
                            </div>
                        </Form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemSettings;
